import {
  Badge,
  Button,
  Dialog,
  IconButton,
  Input,
  Portal,
  Spinner,
  Stack,
  Table,
  Text,
  Card,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { FC, useEffect, useMemo, useState } from 'react';
import { LuTrash, LuUserPlus } from 'react-icons/lu';
import { TeamDetails, TeamMember, useTeamMembers } from './hooks';
import {
  useOrganizationUsersList,
  OrganizationUser,
} from '../users/hooks';

const filterCandidates = (
  users: OrganizationUser[],
  members: TeamMember[],
  query: string
) => {
  const normalized = query.trim().toLowerCase();
  return users
    .filter((user) => !members.some((member) => member.user.id === user.user.id))
    .filter((user) => {
      if (!normalized) return true;
      const name = user.user.name ?? '';
      const email = user.user.email ?? '';
      return (
        name.toLowerCase().includes(normalized) ||
        email.toLowerCase().includes(normalized)
      );
    })
    .slice(0, 10);
};

type ManageTeamMembersModalProps = {
  isOpen: boolean;
  team: TeamDetails | null;
  onClose: () => void;
  onMembersChanged?: () => Promise<void> | void;
};

const ManageTeamMembersModal: FC<ManageTeamMembersModalProps> = ({
  isOpen,
  team,
  onClose,
  onMembersChanged,
}) => {
  const {
    members,
    isLoading,
    loadError,
    refresh,
    addMember,
    removeMember,
    isMutating,
    mutateError,
  } = useTeamMembers(isOpen ? team?.id ?? null : null);
  const {
    users: organizationUsers,
    isLoading: isLoadingUsers,
    error: usersError,
    refresh: refreshUsers,
  } = useOrganizationUsersList();
  const [search, setSearch] = useState('');
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const candidateUsers = useMemo(
    () => filterCandidates(organizationUsers, members, search),
    [organizationUsers, members, search]
  );

  useEffect(() => {
    if (isOpen) {
      void refresh();
      void refreshUsers();
    }
  }, [isOpen, refresh, refreshUsers]);

  const handleAdd = async (userId: string) => {
    if (!team?.id) return;
    setSubmissionError(null);
    try {
      await addMember({ userId });
      toaster.create({
        type: 'success',
        title: 'Member added',
        description: 'User added to the team.',
      });
      setSearch('');
      await onMembersChanged?.();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to add user to team.';
      setSubmissionError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to add user',
        description: message,
      });
    }
  };

  const handleRemove = async (memberId: string) => {
    if (!team?.id) return;
    setSubmissionError(null);
    try {
      await removeMember(memberId);
      toaster.create({
        type: 'success',
        title: 'Member removed',
        description: 'User removed from the team.',
      });
      await onMembersChanged?.();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to remove user from team.';
      setSubmissionError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to remove user',
        description: message,
      });
    }
  };

  const closeModal = () => {
    setSearch('');
    setSubmissionError(null);
    onClose();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          closeModal();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="lg">
            <Dialog.CloseTrigger />
            <Dialog.Header flexDirection={'column'}>
              <Dialog.Title>Manage team members</Dialog.Title>
              <Dialog.Description>
                {team ? `Add or remove members for ${team.name}.` : null}
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap="6">
                <Stack gap="3">
                  <Text fontWeight="medium">Current members</Text>
                  {isLoading ? (
                    <Stack align="center" gap="3" py="4">
                      <Spinner />
                      <Text fontSize="sm" color="fg.muted">
                        Loading members…
                      </Text>
                    </Stack>
                  ) : loadError ? (
                    <Stack gap="3">
                      <Text color="fg.error" fontSize="sm">
                        {loadError}
                      </Text>
                      <Button size="sm" onClick={() => void refresh()}>
                        Try again
                      </Button>
                    </Stack>
                  ) : members.length === 0 ? (
                    <Text fontSize="sm" color="fg.muted">
                      This team has no members yet.
                    </Text>
                  ) : (
                    <Table.ScrollArea>
                      <Table.Root>
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader>Name</Table.ColumnHeader>
                            <Table.ColumnHeader>Email</Table.ColumnHeader>
                            <Table.ColumnHeader>Role</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" width="1%">
                              Actions
                            </Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {members.map((member) => (
                            <Table.Row key={member.id}>
                              <Table.Cell>
                                <Text fontWeight="medium">
                                  {member.user.name ?? '—'}
                                </Text>
                              </Table.Cell>
                              <Table.Cell>
                                <Text fontSize="sm" color="fg.muted">
                                  {member.user.email ?? '—'}
                                </Text>
                              </Table.Cell>
                              <Table.Cell>
                                <Badge variant="subtle">{member.role}</Badge>
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                <IconButton
                                  aria-label={`Remove ${
                                    member.user.email ?? 'user'
                                  }`}
                                  size="sm"
                                  variant="ghost"
                                  colorPalette="red"
                                  onClick={() => void handleRemove(member.id)}
                                  disabled={isMutating}
                                >
                                  <LuTrash />
                                </IconButton>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    </Table.ScrollArea>
                  )}
                </Stack>

                <Stack gap="3">
                  <Text fontWeight="medium">Add member</Text>
                  <Input
                    placeholder="Search organization users by name or email"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    disabled={isLoadingUsers}
                  />
                  {usersError ? (
                    <Stack gap="2">
                      <Text color="fg.error" fontSize="sm">
                        {usersError}
                      </Text>
                      <Button size="sm" onClick={() => void refreshUsers()}>
                        Retry loading users
                      </Button>
                    </Stack>
                  ) : isLoadingUsers ? (
                    <Stack align="center" gap="2" py="2">
                      <Spinner size="sm" />
                      <Text fontSize="xs" color="fg.muted">
                        Loading organization users…
                      </Text>
                    </Stack>
                  ) : candidateUsers.length === 0 ? (
                    <Text fontSize="sm" color="fg.muted">
                      {search
                        ? 'No matching users found.'
                        : 'Everyone in the organization has been added to this team.'}
                    </Text>
                  ) : (
                    <Stack
                      borderWidth="1px"
                      borderRadius="md"
                      maxH="200px"
                      overflow="auto"
                      gap="0"
                    >
                      {candidateUsers.map((candidate) => (
                        <Card.Root
                          key={candidate.id}
                          borderBottomWidth="1px"
                          borderColor="border.subtle"
                          borderRadius="0"
                        >
                          <Card.Body
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            px="3"
                            py="2"
                            flexDirection={'row'}
                          >
                            <Stack gap="0">
                              <Text fontWeight="medium">
                                {candidate.user.name ?? candidate.user.email ?? '—'}
                              </Text>
                              <Text fontSize="xs" color="fg.muted">
                                {candidate.user.email}
                              </Text>
                            </Stack>
                            <Button
                              size="xs"
                              onClick={() => void handleAdd(candidate.user.id)}
                              disabled={isMutating}
                            >
                              <LuUserPlus /> Add
                            </Button>
                          </Card.Body>
                        </Card.Root>
                      ))}
                    </Stack>
                  )}
                </Stack>

                {mutateError || submissionError ? (
                  <Text color="fg.error" fontSize="sm">
                    {submissionError ?? mutateError}
                  </Text>
                ) : null}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer justifyContent="flex-end">
              <Button variant="ghost" onClick={closeModal} disabled={isMutating}>
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ManageTeamMembersModal;
